import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { marketingManifestApi, asyncTasksListing, asyncTaskResult } from "@/network/network";
import { showToast } from "@/other/utils/utils";
import { ASYNC_TASK_TYPES, ASYNC_TASK_STATUS } from "@/other/constants/OtherKeys";

interface SMMGeneratorContextValue {
  manifestContent: string;
  isLoading: boolean;
  isGenerating: boolean;
  isGeneratingTags: boolean;
  tagReviewOpen: boolean;
  setTagReviewOpen: (open: boolean) => void;
}

const SMMGeneratorContext = createContext<SMMGeneratorContextValue | undefined>(undefined);

export const SMMGeneratorProvider = ({ children }: { children: ReactNode }) => {
  const { selectedBoard } = useSwivuBryntumContext();
  const slug = (selectedBoard?.record as any)?.slug as string | undefined;
  const boardId = (selectedBoard?.record as any)?.id as number | undefined;
  const api = useMemo(() => (slug ? marketingManifestApi(slug) : null), [slug]);

  const [manifestContent, setManifestContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingTags, setIsGeneratingTags] = useState(false);
  const [tagReviewOpen, setTagReviewOpen] = useState(false);

  const processedTaskIdsRef = useRef<Set<number>>(new Set());
  const observedPendingTaskIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    processedTaskIdsRef.current.clear();
    observedPendingTaskIdsRef.current.clear();
    setIsGenerating(false);
    setIsGeneratingTags(false);
    setTagReviewOpen(false);
  }, [slug, boardId]);

  const loadManifest = useCallback(async (opts?: { silent?: boolean }) => {
    if (!api) {
      setManifestContent("");
      setIsLoading(false);
      return;
    }
    if (!opts?.silent) setIsLoading(true);
    try {
      const response = await api.get();
      const data = response.data;
      if (data?.detail != null || data?.content == null) {
        setManifestContent("");
        return;
      }
      setManifestContent(data.content || "");
    } catch {
      setManifestContent("");
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadManifest();
  }, [loadManifest]);

  useEffect(() => {
    if (!api || !boardId) return;

    let isMounted = true;

    const acknowledgeTask = async (taskId: number) => {
      try {
        await asyncTaskResult(taskId);
      } catch {
        // Task may already be retrieved; polling should stop either way.
      }
    };

    const handleManifestTask = async (task: any) => {
      if (task.status === ASYNC_TASK_STATUS.PENDING) {
        observedPendingTaskIdsRef.current.add(task.id);
        setIsGenerating(true);
        return;
      }

      if (processedTaskIdsRef.current.has(task.id)) return;

      const sawPending = observedPendingTaskIdsRef.current.has(task.id);

      if (task.status === ASYNC_TASK_STATUS.COMPLETED) {
        processedTaskIdsRef.current.add(task.id);
        if (sawPending) {
          try {
            await loadManifest({ silent: true });
          } catch {
            showToast({ msg: "Failed to retrieve manifest generation result.", type: "error", duration: 3000 });
          }
        }
        if (isMounted) setIsGenerating(false);
        await acknowledgeTask(task.id);
        return;
      }

      if (task.status === ASYNC_TASK_STATUS.CANCELLED || task.status === ASYNC_TASK_STATUS.FAILED) {
        processedTaskIdsRef.current.add(task.id);
        if (isMounted) setIsGenerating(false);
        if (sawPending && task.status === ASYNC_TASK_STATUS.FAILED) {
          showToast({ msg: "Manifest generation failed.", type: "error", duration: 3000 });
        }
        await acknowledgeTask(task.id);
      }
    };

    const handleTagsTask = async (task: any) => {
      if (task.status === ASYNC_TASK_STATUS.PENDING) {
        observedPendingTaskIdsRef.current.add(task.id);
        setIsGeneratingTags(true);
        return;
      }

      if (processedTaskIdsRef.current.has(task.id)) return;

      const sawPending = observedPendingTaskIdsRef.current.has(task.id);

      if (task.status === ASYNC_TASK_STATUS.COMPLETED) {
        processedTaskIdsRef.current.add(task.id);
        if (isMounted) {
          setIsGeneratingTags(false);
          if (sawPending) {
            showToast({ msg: "Marketing tags updated.", type: "success", duration: 3000 });
          }
        }
        await acknowledgeTask(task.id);
        return;
      }

      if (task.status === ASYNC_TASK_STATUS.CANCELLED || task.status === ASYNC_TASK_STATUS.FAILED) {
        processedTaskIdsRef.current.add(task.id);
        if (isMounted) setIsGeneratingTags(false);
        if (sawPending && task.status === ASYNC_TASK_STATUS.FAILED) {
          showToast({ msg: "Tags generation failed.", type: "error", duration: 3000 });
          try {
            await loadManifest({ silent: true });
          } catch {
            // ignore refresh errors
          }
        }
        await acknowledgeTask(task.id);
      }
    };

    const pollAsyncTasks = async () => {
      try {
        const response = await asyncTasksListing();
        if (!isMounted) return;

        const allTasks: any[] = response.data || [];
        const boardTasks = allTasks
          .filter((task: any) => task.meta_data?.board_id === boardId)
          .sort((a: any, b: any) => b.id - a.id);

        const manifestTask = boardTasks.find(
          (task: any) => task.type === ASYNC_TASK_TYPES.MARKETING_MANIFEST_GENERATION && !task.is_retrieved,
        );
        const tagsTask = boardTasks.find(
          (task: any) => task.type === ASYNC_TASK_TYPES.MARKETING_TAGS_GENERATION && !task.is_retrieved,
        );

        if (manifestTask) await handleManifestTask(manifestTask);
        if (tagsTask) await handleTagsTask(tagsTask);
      } catch {
        // Silently ignore polling errors to avoid disrupting the UI
      }
    };

    pollAsyncTasks();
    const intervalId = setInterval(pollAsyncTasks, 10000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [api, boardId, loadManifest]);

  const value: SMMGeneratorContextValue = {
    manifestContent,
    isLoading,
    isGenerating,
    isGeneratingTags,
    tagReviewOpen,
    setTagReviewOpen,
  };

  return <SMMGeneratorContext.Provider value={value}>{children}</SMMGeneratorContext.Provider>;
};

export const useSMMGeneratorContext = () => {
  const context = useContext(SMMGeneratorContext);
  if (context === undefined) {
    throw new Error("useSMMGeneratorContext must be used within a SMMGeneratorProvider");
  }
  return context;
};
